import { useCognitoIdentityProvider } from '@backoffice-frontend/authentication'
import { ENV, Environment } from '@backoffice-frontend/environment'
import { Textfield } from '@backoffice-frontend/forms'
import { MoiaLogo } from '@backoffice-frontend/patterns'
import {
  Button,
  Callout,
  Card,
  FormFieldValidation,
  Text,
} from '@moia-dev/pace-web'
import { type FormEvent, type ReactNode, useState } from 'react'
import { useTranslation } from 'react-i18next'

const baseDomains: Partial<Record<Environment, string>> = {
  [Environment.development]: '.backoffice.dev.moia-group.io',
  [Environment.integration]: '.backoffice.int.moia-group.io',
  [Environment.production]: '.backoffice.moia-group.io',
  [Environment['production.us-east-1']]: '.backoffice.us.moia-group.io',
}

const identityProviderApiBaseUrl: Partial<Record<Environment, string>> = {
  [Environment.development]:
    'https://cognito-identity-provider.backoffice.dev.moia-group.io',
  [Environment.integration]:
    'https://cognito-identity-provider.backoffice.int.moia-group.io',
  [Environment.production]:
    'https://cognito-identity-provider.backoffice.prd.moia-group.io',
  [Environment['production.us-east-1']]:
    'https://cognito-identity-provider.backoffice.prd.us-east-1.moia-group.io',
}

const baseDomain = baseDomains[ENV]
const apiBaseUrl = identityProviderApiBaseUrl[ENV]

export const DomainMigrationRedirect = ({
  children,
}: {
  children: ReactNode
}) => {
  const { t } = useTranslation()
  const [email, setEmail] = useState('')
  const [error, setError] = useState('')
  const { noOrganizationFound } = useCognitoIdentityProvider()

  const isOldDomain = (() => {
    const hostname = window.location.hostname

    if (ENV === Environment.production) {
      return (
        hostname === 'frontend.backoffice.prd.moia-group.io' ||
        hostname.endsWith('.backoffice.prd.moia-group.io')
      )
    }
    if (ENV === Environment.integration) {
      return hostname === 'frontend.backoffice.int.moia-group.io'
    }
    return hostname === 'frontend.backoffice.dev.moia-group.io'
  })()

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()

    if (!email) return

    setError('')
    try {
      const response = await fetch(
        `${apiBaseUrl}/backoffice-url?email=${encodeURIComponent(email)}`,
      )

      if (!response.ok) {
        throw new Error('Failed to find organization for this email')
      }

      const data = (await response.json()) as { subdomain: string }

      if (data.subdomain) {
        window.location.href = `https://${data.subdomain}${baseDomain}/login`
        return
      }

      setError(t('loginView_oldDomainRedirect_noOrganizationFound'))
    } catch (err) {
      console.error('Error redirecting:', err)
      setError(t('loginView_oldDomainRedirect_error'))
    }
  }

  if (!isOldDomain && !noOrganizationFound) {
    return <>{children}</>
  }

  return (
    <>
      <title>{t('loginView_oldDomainRedirect_title')}</title>
      <div
        css={{
          minHeight: '100vh',
          backgroundSize: 'cover',
          alignItems: 'flex-start',
          justifyItems: 'center',
          backgroundImage: `url(/assets/images/login.png)`,
          display: 'grid',
        }}
      >
        <MoiaLogo
          css={theme => ({
            width: '160px',
            marginTop: theme.spacing(6),
            fill: theme.palette.common.white,
          })}
        />
        <Card
          css={theme => ({
            padding: theme.spacing(3),
            width: '400px',
            display: 'flex',
            flexDirection: 'column',
            gap: theme.spacing(2),
          })}
          outline
        >
          {isOldDomain && (
            <Callout variant="warning" hideDismiss>
              <Text>{t('loginView_oldDomainRedirect_callout')}</Text>
            </Callout>
          )}
          {noOrganizationFound && (
            <Callout variant="critical" hideDismiss>
              <Text>{t('loginView_invalidDomain_callout')}</Text>
            </Callout>
          )}
          <form
            onSubmit={handleSubmit}
            css={theme => ({
              display: 'flex',
              flexDirection: 'column',
              gap: theme.spacing(2),
            })}
          >
            <Textfield
              label={t('common_email_label')}
              required
              value={email}
              onChange={e => setEmail(e.target.value)}
            />
            {error && (
              <FormFieldValidation type="error">{error}</FormFieldValidation>
            )}
            <Button
              type="submit"
              label={t('loginView_domainRedirect_button')}
              disabled={!email}
            />
          </form>
        </Card>
      </div>
    </>
  )
}
